/**
 * SurveyPublicView - Public Survey Response Page
 *
 * Allows anyone to fill out and submit a survey without authentication.
 * Accessed via direct link: https://zapojsedovoleb.cz/{survey_id}
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSurvey, submitSurveyResponse } from '../../services/api';
import { SurveyTemplate, Question } from '../../types';

// Section labels in Czech
const SECTION_LABELS: Record<string, string> = {
  filtering: 'Úvodní otázky',
  satisfaction: 'Hodnocení stavu obce',
  priorities: 'Priority a očekávání',
  values: 'Postoje a hodnoty',
  trust: 'Vnímání politiky a důvěra',
  communication: 'Komunikační kanály',
  open_questions: 'Otevřené otázky',
  demographics: 'O vás',
  local_issues: 'Místní témata',
  campaign: 'Kampaň'
};

// Default Likert labels
const DEFAULT_LIKERT_LABELS: Record<string, string> = {
  '1': 'Rozhodně nesouhlasím',
  '2': 'Spíše nesouhlasím',
  '3': 'Nevím / neutrální',
  '4': 'Spíše souhlasím',
  '5': 'Rozhodně souhlasím'
};

/**
 * Detect max_selections from question text for legacy surveys.
 * Patterns: "3 oblasti", "vyberte 3", "TOP 3", "které 3", etc.
 */
const detectMaxSelections = (question: Question): number | undefined => {
  // First check if explicitly set
  if (question.max_selections) return question.max_selections;

  // Only for choice questions
  if (question.type !== 'choice') return undefined;

  // Detect patterns in question text
  const text = question.text.toLowerCase();
  const patterns = [
    /(\d+)\s*oblast/i,      // "3 oblasti"
    /vyberte\s*(\d+)/i,     // "vyberte 3"
    /top\s*(\d+)/i,         // "TOP 3"
    /které\s*(\d+)/i,       // "které 3"
    /(\d+)\s*nejdůležitějš/i, // "3 nejdůležitější"
    /(\d+)\s*priorit/i,     // "3 priority"
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const num = parseInt(match[1], 10);
      if (num >= 2 && num <= 10) return num;
    }
  }

  return undefined;
};

const SurveyPublicView: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<SurveyTemplate | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  // Group questions by section
  const sections = React.useMemo(() => {
    if (!survey) return [];
    const grouped: { section: string; questions: Question[] }[] = [];
    let currentSectionName = '';

    for (const q of survey.questions) {
      if (q.section !== currentSectionName) {
        currentSectionName = q.section;
        grouped.push({ section: q.section, questions: [] });
      }
      grouped[grouped.length - 1].questions.push(q);
    }

    return grouped;
  }, [survey]);

  // Load survey
  useEffect(() => {
    const loadSurvey = async () => {
      if (!surveyId) {
        setError('Chybí ID dotazníku');
        setLoading(false);
        return;
      }

      try {
        const data = await getSurvey(surveyId);
        setSurvey(data);
      } catch (err: any) {
        setError(err.message || 'Nepodařilo se načíst dotazník');
      } finally {
        setLoading(false);
      }
    };

    loadSurvey();
  }, [surveyId]);

  // Handle answer change
  const handleAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Check if current section is complete (all required questions answered)
  const isSectionComplete = (sectionIndex: number): boolean => {
    if (!sections[sectionIndex]) return true;
    const sectionQuestions = sections[sectionIndex].questions;
    return sectionQuestions.every(q => {
      if (!q.required) return true;
      const answer = answers[q.id];
      if (answer === undefined || answer === null || answer === '') return false;
      if (Array.isArray(answer) && answer.length === 0) return false;
      return true;
    });
  };

  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSection]);

  // Count answered questions
  const answeredCount = Object.keys(answers).filter(k => {
    const v = answers[k];
    return v !== undefined && v !== null && v !== '' && (!Array.isArray(v) || v.length > 0);
  }).length;

  const totalRequired = survey?.questions.filter(q => q.required).length || 0;

  // Submit survey
  const handleSubmit = async () => {
    if (!surveyId) return;

    // Validate all required questions
    const unanswered = survey?.questions.filter(q => {
      if (!q.required) return false;
      const answer = answers[q.id];
      if (answer === undefined || answer === null || answer === '') return true;
      if (Array.isArray(answer) && answer.length === 0) return true;
      return false;
    });

    if (unanswered && unanswered.length > 0) {
      setError(`Prosím vyplňte všechny povinné otázky (chybí ${unanswered.length})`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitSurveyResponse(surveyId, answers);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Nepodařilo se odeslat odpovědi');
    } finally {
      setSubmitting(false);
    }
  };

  // Render question based on type
  const renderQuestion = (question: Question) => {
    const value = answers[question.id];

    switch (question.type) {
      case 'likert':
        const labels = question.likert_labels || DEFAULT_LIKERT_LABELS;
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleAnswer(question.id, num)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    value === num
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <span className="text-xl font-semibold">{num}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-500 px-1">
              <span>{labels['1']}</span>
              <span>{labels['5']}</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-4">
            {['ano', 'ne'].map(option => (
              <button
                key={option}
                type="button"
                onClick={() => handleAnswer(question.id, option)}
                className={`flex-1 py-4 px-6 rounded-xl text-lg font-medium transition-all ${
                  value === option
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {option === 'ano' ? 'Ano' : 'Ne'}
              </button>
            ))}
          </div>
        );

      case 'choice':
        if (!question.options) return null;
        // Use explicit max_selections or detect from question text (for legacy surveys)
        const maxSelections = detectMaxSelections(question);
        const isMultiSelect = maxSelections && maxSelections > 1;
        const selectedArray = Array.isArray(value) ? value : [];

        // Check if "other" is selected (value starts with "other:")
        const otherPrefix = 'other:';
        const hasOtherSelected = isMultiSelect
          ? selectedArray.some(v => typeof v === 'string' && v.startsWith(otherPrefix))
          : typeof value === 'string' && value.startsWith(otherPrefix);
        const otherText = isMultiSelect
          ? (selectedArray.find(v => typeof v === 'string' && v.startsWith(otherPrefix)) || '').replace(otherPrefix, '')
          : (hasOtherSelected ? (value as string).replace(otherPrefix, '') : '');

        // For max count, count "other" as one selection
        const effectiveSelectedCount = isMultiSelect
          ? selectedArray.filter(v => typeof v === 'string' && !v.startsWith(otherPrefix)).length + (hasOtherSelected ? 1 : 0)
          : 0;
        const maxReached = isMultiSelect && effectiveSelectedCount >= (maxSelections || 1);

        const handleChoiceClick = (option: string) => {
          if (isMultiSelect) {
            // Multi-select: toggle option in array
            if (selectedArray.includes(option)) {
              handleAnswer(question.id, selectedArray.filter(o => o !== option));
            } else if (!maxReached) {
              handleAnswer(question.id, [...selectedArray, option]);
            }
          } else {
            // Single-select
            handleAnswer(question.id, option);
          }
        };

        const handleOtherToggle = () => {
          if (isMultiSelect) {
            if (hasOtherSelected) {
              // Remove other from array
              handleAnswer(question.id, selectedArray.filter(v => !v.startsWith(otherPrefix)));
            } else if (!maxReached) {
              // Add empty other to array
              handleAnswer(question.id, [...selectedArray, otherPrefix]);
            }
          } else {
            // Single-select: toggle other
            if (hasOtherSelected) {
              handleAnswer(question.id, undefined);
            } else {
              handleAnswer(question.id, otherPrefix);
            }
          }
        };

        const handleOtherTextChange = (text: string) => {
          if (isMultiSelect) {
            // Update other value in array
            const withoutOther = selectedArray.filter(v => !v.startsWith(otherPrefix));
            handleAnswer(question.id, [...withoutOther, otherPrefix + text]);
          } else {
            handleAnswer(question.id, otherPrefix + text);
          }
        };

        const isOtherDisabled = Boolean(isMultiSelect && maxReached && !hasOtherSelected);

        return (
          <div className="space-y-2">
            {isMultiSelect && (
              <p className="text-sm text-gray-500 mb-3">
                Vyberte {maxSelections} možnosti ({effectiveSelectedCount}/{maxSelections})
              </p>
            )}
            {question.options.map((option, idx) => {
              const isSelected = isMultiSelect
                ? selectedArray.includes(option)
                : value === option;
              const isDisabled = Boolean(isMultiSelect && maxReached && !isSelected);

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleChoiceClick(option)}
                  disabled={isDisabled}
                  className={`w-full text-left p-4 rounded-xl transition-all flex items-center gap-3 ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-lg'
                      : isDisabled
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {isMultiSelect && (
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-white border-white' : 'border-gray-300'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                  )}
                  {option}
                </button>
              );
            })}

            {/* "Jiné" (Other) option */}
            <div className="pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleOtherToggle}
                disabled={isOtherDisabled}
                className={`w-full text-left p-4 rounded-xl transition-all flex items-center gap-3 ${
                  hasOtherSelected
                    ? 'bg-blue-600 text-white shadow-lg'
                    : isOtherDisabled
                      ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isMultiSelect && (
                  <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    hasOtherSelected ? 'bg-white border-white' : 'border-gray-300'
                  }`}>
                    {hasOtherSelected && (
                      <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                )}
                Jiné...
              </button>

              {hasOtherSelected && (
                <input
                  type="text"
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  placeholder="Napište svou odpověď..."
                  className="w-full mt-2 p-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  autoFocus
                />
              )}
            </div>
          </div>
        );

      case 'open':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Vaše odpověď..."
            rows={4}
            className="w-full p-4 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
          />
        );

      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p>Načítám dotazník...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Chyba</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            Zpět na hlavní stránku
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Děkujeme!</h1>
          <p className="text-gray-600 mb-6">
            Vaše odpovědi byly úspěšně odeslány. Děkujeme za váš čas a názory.
          </p>
          <p className="text-sm text-gray-500">
            Vaše odpovědi pomohou zlepšit naši obec.
          </p>
        </div>
      </div>
    );
  }

  // Survey form
  if (!survey) return null;

  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;
  const canProceed = isSectionComplete(currentSection);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-6 px-4 shadow-lg">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl md:text-2xl font-bold mb-1">{survey.title}</h1>
          <p className="text-blue-100 text-sm">{survey.municipality_name}</p>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Sekce {currentSection + 1} z {sections.length}</span>
            <span>{answeredCount} z {totalRequired} povinných</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Description */}
        {currentSection === 0 && survey.description && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
            <p className="text-blue-800 text-sm">{survey.description}</p>
          </div>
        )}

        {/* GDPR / Anonymity notice — shown on first section */}
        {currentSection === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Anonymní průzkum
                </p>
                <p className="text-xs text-gray-500">
                  Tento dotazník je zcela anonymní. Sbíráme názory občanů,
                  aby je politické strany a kandidáti mohli zohlednit
                  při tvorbě svého programu pro obec {survey.municipality_name}.
                  Odpovědi nelze zpětně přiřadit ke konkrétní osobě.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Section title */}
        {currentSectionData && (
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {SECTION_LABELS[currentSectionData.section] || currentSectionData.section}
          </h2>
        )}

        {/* Questions */}
        <div className="space-y-8">
          {currentSectionData?.questions.map((question, idx) => (
            <div key={question.id} className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">
                    {question.text}
                    {question.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                </div>
              </div>
              {renderQuestion(question)}
            </div>
          ))}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 gap-4">
          <button
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              currentSection === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Zpět
          </button>

          {isLastSection ? (
            <button
              onClick={handleSubmit}
              disabled={submitting || !canProceed}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
                submitting || !canProceed
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
              }`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Odesílám...
                </span>
              ) : (
                'Odeslat odpovědi'
              )}
            </button>
          ) : (
            <button
              onClick={() => setCurrentSection(prev => Math.min(sections.length - 1, prev + 1))}
              disabled={!canProceed}
              className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
                !canProceed
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
              }`}
            >
              Pokračovat
            </button>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-gray-400 text-xs">
        <p>Phoenix Electoral Analytics</p>
      </footer>
    </div>
  );
};

export default SurveyPublicView;
